import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { v4 as uuid } from 'uuid';
import { Observable, Subject } from 'rxjs';
import Swal from 'sweetalert2';
import { AttributeMapperService } from './attribute-mapper.service';

@Component({
  selector: 'attribute-mapper',
  standalone: true,
  imports: [CommonModule, NgxPaginationModule, FormsModule],
  templateUrl: './attribute-mapper.component.html',
  styleUrls: ['./attribute-mapper.component.css'],
})
export class AttributeMapperComponent {
  constructor() {}
  isSection = false;
  toggleJsonStructure = false;
  editingExistingRecord = false;

  questionsList: any[] = [];
  attributeMappingsPayload: any = [];
  currentFormFieldAttrMappings: any = null;

  private _formId = 0;
  public get formId() {
    let localStorageFormId = localStorage.getItem('FormId');
    return (this._formId = Number(JSON.parse(localStorageFormId)) ?? 0);
  }

  currentPageNumber: number = 1;
  numberOfItemsPerPage: number = 5;
  totalNumberOfItems: number = this.questionsList.length;

  allQuestionsFromLocalStore: any;

  fieldsWithDuplicateAttributeNames = [];

  private _attributeMapperService = inject(AttributeMapperService);

  private _helper$: Subject<string> = new Subject();
  public get helper(): Observable<string> {
    return this._helper$.asObservable();
  }
  public set helper(value: string) {
    this._helper$.next(value);
  }

  res = [];
  getLocallyStoredQuestions() {
    if (this.isSection) {
      if (localStorage.getItem('AllQuestions')) {
        this.allQuestionsFromLocalStore = JSON.parse(
          localStorage.getItem('AllQuestions') ?? null
        );
      }
      this.allQuestionsFromLocalStore[0]?.Section?.forEach((currSec) => {
        if (currSec?.Sections?.length) {
          currSec?.Sections?.forEach((subSec) => {
            if (subSec?.Fields?.length) {
              this.res = [...this.res, ...subSec?.Fields];
            }
          });
        }
        if (currSec?.Fields?.length) {
          this.res = [...this.res, ...currSec?.Fields];
        }
      });

      // Content blocks (Info/intro) are non-mappable (IsMappable === false / __contentBlock)
      // — they're display text, not fields, so keep them out of the Attribute Mapper.
      this.questionsList = [...this.res].filter(
        (f: any) => !(f?.IsMappable === false || f?.__contentBlock === true)
      );
      this.totalNumberOfItems = this.questionsList.length;
    } else {
      if (localStorage.getItem('AllQuestions')) {
        this.allQuestionsFromLocalStore = JSON.parse(
          localStorage.getItem('AllQuestions') ?? null
        );

        this.questionsList =
          this.allQuestionsFromLocalStore[0].Section[0].Fields.filter(
            (f: any) => !(f?.IsMappable === false || f?.__contentBlock === true)
          );
        this.totalNumberOfItems = this.questionsList.length;
      }
    }
  }

  bindAttributesForCurrentField(field) {
    // API returns camelCase: data, not Data (ASP.NET Core default JSON serialization)
    const mappingData = this.currentFormFieldAttrMappings?.data || this.currentFormFieldAttrMappings?.Data;
    
    if (
      this.currentFormFieldAttrMappings &&
      mappingData &&
      mappingData.length !== 0
    ) {
      // Compare GUIDs case-insensitively since database/API may return different casing
      const fieldUniqueIdLower = field.UniqueID?.toLowerCase();
      const attrMapppingFetched = mappingData?.filter(
        (resultObj) => {
          const attrValue = (resultObj.attributeValue || resultObj.AttributeValue || '').toLowerCase();
          return attrValue === fieldUniqueIdLower;
        }
      );

      if (attrMapppingFetched.length === 0) {
        field.Attributes = [
          {
            AttributeValue: field.UniqueID,
            AttributeName: '',
            UniqueId: uuid(),
          },
        ];
      } else {
        if (attrMapppingFetched[0]) {
          const mapping = attrMapppingFetched[0];
          field.Attributes = [
            {
              AttributeValue: mapping.attributeValue || mapping.AttributeValue,
              AttributeName: mapping.attributeName || mapping.AttributeName,
              UniqueId: mapping.uniqueId || mapping.UniqueId,
            },
          ];
        }
      }
    } else {
      // No mapping data exists - initialize with empty attribute
      field.Attributes = [
        {
          AttributeValue: field.UniqueID,
          AttributeName: '',
          UniqueId: uuid(),
        },
      ];
    }
  }

  ngOnInit() {
    const sectionFlag = localStorage.getItem('isSection');
    this.isSection = JSON.parse(sectionFlag);

    this.getLocallyStoredQuestions();

    // SM-4508 fix: initialise field.Attributes / the payload with empty slots
    // BEFORE the async getAttrMaps() call returns so the template always has
    // a valid binding target (qn.Attributes[0].AttributeName) while waiting.
    this.cookAttributeMappingsPayload();

    // bindAttributesForCurrentField() (inside getAttrMaps' success handler)
    // overwrites those empty slots with persisted values once the response
    // arrives, and cookAttributeMappingsPayload() is called again there to
    // rebuild the payload from those values.
    this.getAttrMaps();
  }

  onPageChange(event: number) {
    this.currentPageNumber = event;
    this.saveAttrMaps('local');
  }

  getAttrMaps(storage?: string) {
    this._attributeMapperService.getAttributeMappings(this.formId).subscribe({
      next: (resp) => {
        this.currentFormFieldAttrMappings = resp;
        this.helper = 'FETCHED_ATTRIBUTE_MAPPINGS_SUCCESSFULLY';
        //Add Attributes Object to all the fields
        this.questionsList.forEach((field: any) => {
          this.bindAttributesForCurrentField(field);
        });
        // SM-4508 fix: rebuild the payload AFTER binding so that
        // attributeMappingsPayload reflects the values just loaded from the
        // server, not the empty stubs set during ngOnInit.
        this.cookAttributeMappingsPayload();
      },
      error: (data) => {
        this.helper = 'FAILED_TO_FETCH_ATTRIBUTE_MAPPINGS';
        // Handle both camelCase and PascalCase response properties
        const isSuccess = data.isSuccess || data.IsSuccess;
        const message = data.message || data.Message;
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            confirmButton: 'btn',
          },
          buttonsStyling: false,
        });
        swalWithBootstrapButtons.fire({
          title: 'Failure!',
          text:
            !isSuccess || (message && message !== '')
              ? message
              : 'Cannot fetch field attribute mappings.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      },
    });
  }

  saveAttrMaps(storage?: string) {
    //Before save set IsMappingExists: true for all the fields which have Attributes defined
    this.setMappingExistsIfAttributesMapped();

    this.fieldsWithDuplicateAttributeNames =
      this.getFieldsWithDuplicateAttributeNames();

    // and if no duplicate attributes exist
    if (this.fieldsWithDuplicateAttributeNames.length === 0) {
      this.cookAttributeMappingsPayload();
      // then save

      this.fieldsWithDuplicateAttributeNames =
        this.getFieldsWithDuplicateAttributeNames();

      if (storage && storage.toLowerCase() === 'database') {
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            confirmButton: 'btn',
          },
          buttonsStyling: false,
        });
        this._attributeMapperService
          .saveAttributeMappings(this.attributeMappingsPayload)
          .subscribe({
            next: (data: any) => {
              // API returns lowercase: isSuccess, message (not IsSuccess, Message)
              const isSuccess = data.isSuccess || data.IsSuccess;
              const message = data.message || data.Message;
              swalWithBootstrapButtons
                .fire({
                  title: isSuccess ? 'Success!' : 'Failure!',
                  text:
                    isSuccess && (!message || message === '')
                      ? 'Field attribute mapping is successful'
                      : message || 'Field attribute mapping failed',
                  icon: isSuccess ? 'success' : 'error',
                  confirmButtonText: 'OK',
                })
                .then(() => {
                  if (isSuccess) {
                    // SM-4508 fix: re-fetch saved mappings after a successful
                    // save to confirm the data was persisted and keep the
                    // component state in sync with the server. Without this,
                    // navigation-away destroyed the component and on return
                    // the inputs appeared empty despite the success dialog.
                    this.getAttrMaps();
                  }
                });
            },
            error: (err: any) => {
              swalWithBootstrapButtons.fire({
                title: 'Failure!',
                text: 'Field attribute mapping is unsuccessful',
                icon: 'error',
                confirmButtonText: 'OK',
              });
            },
          });
      }
    }
  }

  setMappingExistsIfAttributesMapped() {
    this.questionsList.forEach((field: any) => {
      // make the corresponding field.IsMappingExists to true
      if (
        field.Attributes &&
        field.Attributes[0].AttributeName !== null &&
        field.Attributes[0].AttributeName !== ''
      ) {
        field.IsMappingExists = true;
      }
    });
  }

  getFieldsWithDuplicateAttributeNames(): any[] {
    const lookupDuplicates = this.questionsList?.reduce((acc, currVal) => {
      if (
        currVal.Attributes &&
        currVal.Attributes[0].AttributeName !== null &&
        currVal.Attributes[0].AttributeName !== ''
      ) {
        acc[currVal.Attributes[0].AttributeName] =
          ++acc[currVal.Attributes[0].AttributeName] || 0;
      }
      return acc;
    }, {});

    let fieldsWithDuplicateAttributeNames = this.questionsList?.filter(
      (f) =>
        f.Attributes &&
        f.Attributes[0] &&
        lookupDuplicates[f.Attributes[0].AttributeName]
    );
    return fieldsWithDuplicateAttributeNames;
  }

  cookAttributeMappingsPayload() {
    return (this.attributeMappingsPayload = {
      TemplateID: this.allQuestionsFromLocalStore[0].Section[0].TemplateID,
      FormID: this.formId,
      Attributes: this.questionsList
        ?.filter((field: any) => field.IsMappable)
        .map((field: any) => {
          if (field.Attributes && field.Attributes[0]) {
            return field.Attributes[0];
          } else {
            field.Attributes = [
              {
                AttributeValue: field.UniqueID,
                AttributeName: '',
                UniqueId: uuid(),
              },
            ];
            return field.Attributes[0];
          }
        }),
    });
  }
}
